import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@workspace/ui/lib/utils";
import { Icon } from "../atoms/icon.js";

const aiGenerateButtonVariants = cva(
    "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90",
                outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                ghost: "hover:bg-accent hover:text-accent-foreground",
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

export interface AIGenerateButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof aiGenerateButtonVariants> {
    loading?: boolean;
    onGenerate?: () => Promise<void> | void;
    generatedContent?: string;
    placeholder?: string;
}

export const AIGenerateButton = React.forwardRef<HTMLButtonElement, AIGenerateButtonProps>(
    ({
        className,
        variant,
        size,
        loading = false,
        onGenerate,
        generatedContent,
        placeholder = "Generate with AI",
        children,
        onClick,
        disabled,
        ...props
    }, ref) => {
        const [isLoading, setIsLoading] = React.useState(loading);
        const [hasGenerated, setHasGenerated] = React.useState(false);

        React.useEffect(() => {
            setIsLoading(loading);
        }, [loading]);

        React.useEffect(() => {
            setHasGenerated(!!generatedContent);
        }, [generatedContent]);

        const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
            if (disabled || isLoading) return;

            setIsLoading(true);
            try {
                await onGenerate?.();
                onClick?.(event);
            } catch (error) {
                console.error('AI generation failed:', error);
            } finally {
                setIsLoading(false);
            }
        };

        return (
            <button
                ref={ref}
                className={cn(aiGenerateButtonVariants({ variant, size }), className)}
                onClick={handleClick}
                disabled={disabled || isLoading}
                {...props}
            >
                {isLoading ? (
                    <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        <span>Generating...</span>
                    </>
                ) : hasGenerated ? (
                    <>
                        <Icon name="check-circle" size="sm" />
                        <span>Regenerate</span>
                    </>
                ) : (
                    <>
                        <div className="h-4 w-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
                        <span>{children || placeholder}</span>
                    </>
                )}
            </button>
        );
    }
);

AIGenerateButton.displayName = "AIGenerateButton";
